import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  UserPlus, 
  Building2, 
  Mail, 
  User,
  Clock,
  CheckCircle,
  XCircle,
  Trash2,
  Send
} from 'lucide-react';
import { useAuth, API } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from 'sonner';
import { trackEvent } from '@/lib/posthog';

export default function InviteAdminPage() {
  const [buildings, setBuildings] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, invitation: null });
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    building_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [buildingsRes, invitationsRes] = await Promise.all([
        axios.get(`${API}/super-admin/buildings`),
        axios.get(`${API}/super-admin/invitations`)
      ]);
      setBuildings(buildingsRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.building_id) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/super-admin/invite-admin`, formData);
      toast.success('Invitación enviada exitosamente');
      // Track invitation sent
      trackEvent('super_admin_invite_sent', { building_id: formData.building_id });
      trackEvent('admin_invited', { building_id: formData.building_id });
      setFormData({ email: '', name: '', building_id: '' });
      fetchData();
    } catch (error) {
      console.error('Error sending invitation:', error);
      const message = error.response?.data?.detail || 'Error al enviar invitación';
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/super-admin/invitations/${deleteDialog.invitation?.id}`);
      toast.success('Invitación cancelada');
      setDeleteDialog({ open: false, invitation: null });
      fetchData();
    } catch (error) {
      console.error('Error deleting invitation:', error);
      toast.error('Error al cancelar invitación');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Pendiente' },
      accepted: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle, label: 'Aceptada' },
      expired: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircle, label: 'Expirada' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <badge.icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invite-admin-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invitar Administrador</h1>
          <p className="text-slate-500 mt-1">Invita a un nuevo administrador para gestionar un conjunto</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <Card className="lg:col-span-1 border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-orange-500" />
                Nueva Invitación
              </CardTitle>
              <CardDescription>
                La invitación expira en 48 horas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre del Admin</Label>
                  <Input
                    id="name"
                    placeholder="Nombre completo"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="admin-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@ejemplo.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="admin-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="building">Conjunto a Administrar</Label>
                  <Select 
                    value={formData.building_id} 
                    onValueChange={(value) => setFormData({...formData, building_id: value})}
                  >
                    <SelectTrigger data-testid="building-select">
                      <SelectValue placeholder="Selecciona un conjunto" />
                    </SelectTrigger>
                    <SelectContent>
                      {buildings.map((building) => (
                        <SelectItem key={building.id} value={building.id}>
                          {building.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  disabled={sending}
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  data-testid="send-invitation-btn"
                >
                  {sending ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Enviando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      Enviar Invitación
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Invitations List */}
          <Card className="lg:col-span-2 border-slate-200">
            <CardHeader>
              <CardTitle>Historial de Invitaciones</CardTitle>
              <CardDescription>
                {invitations.length} invitación(es) registrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {invitations.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No hay invitaciones registradas</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {invitations.map((invitation) => (
                    <div 
                      key={invitation.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100"
                      data-testid={`invitation-${invitation.id}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{invitation.name}</span>
                          {getStatusBadge(invitation.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {invitation.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {invitation.building_name}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          Enviada: {formatDate(invitation.created_at)}
                          {invitation.status === 'pending' && (
                            <span className="ml-2">
                              · Expira: {formatDate(invitation.expires_at)}
                            </span>
                          )}
                        </p>
                      </div>

                      {invitation.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteDialog({ open: true, invitation })}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          data-testid={`cancel-invitation-${invitation.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, invitation: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancelar Invitación</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de cancelar la invitación para {deleteDialog.invitation?.name}?
                El enlace de invitación dejará de funcionar.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, invitation: null })}>
                No, mantener
              </Button>
              <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
                Sí, cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
